import React from 'react';

import { UserOutlined } from '@ant-design/icons';
import { Modal, Form, Input, Button, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

interface OperatorVerificationFormValues {
  qc_account: string;
  parcel_account: string;
}

interface OperatorVerificationModalProps {
  open: boolean;
  onVerify: (qcAccount: string, parcelAccount: string) => void | Promise<void>;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  initialQcAccount?: string;
  initialParcelAccount?: string;
}

/**
 * QC 操作人員驗證 Modal
 * 用於驗證 QC 人員和包裝人員帳號
 */
const OperatorVerificationModal: React.FC<OperatorVerificationModalProps> = ({
  open,
  onVerify,
  loading = false,
  error = null,
  onCancel,
  initialQcAccount = '',
  initialParcelAccount = '',
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<OperatorVerificationFormValues>();

  // 當 Modal 開啟且有初始值時，設定表單
  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({
        qc_account: initialQcAccount,
        parcel_account: initialParcelAccount,
      });
    }
  }, [open, initialQcAccount, initialParcelAccount, form]);

  const handleSubmit = async (values: OperatorVerificationFormValues) => {
    try {
      await onVerify(values.qc_account, values.parcel_account);
    } catch {
      // 錯誤已在 context 中處理
    }
  };

  return (
    <Modal
      centered
      open={open}
      closable={true}
      maskClosable={true}
      keyboard={false}
      footer={null}
      width={500}
      destroyOnHidden
      onCancel={onCancel}
    >
      <div style={{ padding: '8px 0' }}>
        <div style={{ marginBottom: '24px', textAlign: 'center' }}>
          <UserOutlined
            style={{
              fontSize: '48px',
              color: '#1890ff',
              marginBottom: '16px',
            }}
          />
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
            {t('operator.qc.list.verification_modal.title')}
          </h3>
          <p style={{ color: '#666', marginTop: '8px', marginBottom: 0 }}>
            {t('operator.qc.list.verification_modal.description')}
          </p>
        </div>

        {error && (
          <Alert
            message={t(`operator.errors.${error}`, { defaultValue: error }) as string}
            type="error"
            showIcon
            closable
            style={{ marginBottom: '20px' }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="qc_account"
            label={t('operator.qc.list.verification_modal.qc_operator')}
            rules={[
              {
                required: true,
                message: t('operator.qc.list.verification_modal.qc_operator_required'),
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('operator.qc.list.verification_modal.qc_operator_placeholder')}
              size="large"
              autoFocus
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="parcel_account"
            label={t('operator.qc.list.verification_modal.parcel_operator')}
            rules={[
              {
                required: true,
                message: t('operator.qc.list.verification_modal.parcel_operator_required'),
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('operator.qc.list.verification_modal.parcel_operator_placeholder')}
              size="large"
              disabled={loading}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              {t('operator.qc.list.verification_modal.verify_button')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default OperatorVerificationModal;
